import React, { useState, useMemo } from 'react';
import { Calendar, Copy, Trash2, Save, Search, CalendarOff, CalendarCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConfirmationDialog } from '@/components/settings/ui/ConfirmationDialog';
import SaveDayTemplateDialog from './SaveDayTemplateDialog';
import DayTemplateItem from './DayTemplateItem';
import { AdvancedShiftCopyDialog } from './AdvancedShiftCopyDialog';
import { DayShift, DayTemplate, BusinessClosedDate, BusinessOpeningHour } from '@/types';
import { toast } from '@/hooks/ui/use-toast';
import { format } from 'date-fns';
import { getNonHistoricalShiftsForDate } from '@/lib/scheduling/shift-filtering-utils';
import { useUnifiedShift } from '@/components/shifts/context/ComposedShiftProvider';

interface DayGroupActionsProps {
  businessId: string;
  selectedDate?: Date; // Keep for backward compatibility
  selectedEditingDate?: Date | null;
  isEditingMode?: boolean;
  allDayShifts: DayShift[];
  onDaySelect?: (date: Date) => void;
  onClearAllShifts?: (date: Date) => Promise<void>;
  onShiftCreated?: () => void;
  onSaveDayTemplate?: (name: string, description: string) => Promise<void>;
  // Day template props
  dayTemplates: DayTemplate[];
  loadingDayTemplates: boolean;
  onEditDayTemplate: (templateId: string) => void;
  onDeleteDayTemplate: (templateId: string) => void;
  onApplyDayTemplate: (template: DayTemplate, targetDate: Date, replace: boolean) => void;
  // Opening hours for closed date functionality (optional, used for display)
  openingHours?: BusinessOpeningHour[] | null;
  // Callback when closed dates change (for parent to refresh)
  onClosedDatesChange?: () => void;
}

const DayGroupActions: React.FC<DayGroupActionsProps> = ({
  businessId,
  selectedDate,
  selectedEditingDate,
  isEditingMode = false,
  allDayShifts,
  onDaySelect,
  onClearAllShifts,
  onShiftCreated,
  onSaveDayTemplate,
  dayTemplates,
  loadingDayTemplates,
  onEditDayTemplate,
  onDeleteDayTemplate,
  onApplyDayTemplate,
  openingHours,
  onClosedDatesChange,
}) => {
  const [isClearing, setIsClearing] = useState(false);
  const [isClearDialogOpen, setIsClearDialogOpen] = useState(false);
  const [showSaveDayTemplateDialog, setShowSaveDayTemplateDialog] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [isClosedDialogOpen, setIsClosedDialogOpen] = useState(false);
  const [closedReason, setClosedReason] = useState('');
  const [isMarkingClosed, setIsMarkingClosed] = useState(false);

  // Get closed dates from ShiftDataProvider context - ensures real-time updates
  // across all components that share this context
  const {
    closedDatesMap,
    addClosedDate,
    removeClosedDate,
    isDateClosed,
    checkExistingShifts
  } = useUnifiedShift();
  
  // Determine the current working date - prioritize selectedEditingDate, fallback to selectedDate
  const currentWorkingDate = selectedEditingDate || selectedDate || new Date();

  // Check if current working date is closed
  const currentDateClosureStatus = useMemo(() => {
    return isDateClosed(currentWorkingDate);
  }, [currentWorkingDate, isDateClosed]);

  // Get custom closed date info if it exists
  const currentDateClosedInfo = useMemo(() => {
    const dateStr = format(currentWorkingDate, 'yyyy-MM-dd');
    return closedDatesMap.get(dateStr);
  }, [currentWorkingDate, closedDatesMap]);

  // Filter day templates based on search term
  const filteredDayTemplates = useMemo(() => {
    if (!templateSearchTerm.trim()) return dayTemplates;

    const term = templateSearchTerm.toLowerCase().trim();
    return dayTemplates.filter(template =>
      template.name.toLowerCase().includes(term) ||
      (template.description || '').toLowerCase().includes(term)
    );
  }, [dayTemplates, templateSearchTerm]);

  // Get shifts for the currently working date
  const shiftsForWorkingDate = useMemo(() => {
    // Use unified utility function for consistent filtering
    return getNonHistoricalShiftsForDate(allDayShifts, currentWorkingDate);
  }, [allDayShifts, currentWorkingDate]);

  const handleClearDay = async () => {
    if (!onClearAllShifts) {
      toast.error('Clear functionality not available');
      setIsClearDialogOpen(false);
      return;
    }

    setIsClearing(true);
    try {
      await onClearAllShifts(currentWorkingDate);
      toast.success(`Successfully cleared ${shiftsForWorkingDate.length} shift(s) from ${format(currentWorkingDate, 'MMM d, yyyy')}`);
      setIsClearDialogOpen(false);
    } catch (error) {
      console.error('Clear day failed:', error);
      toast.error('Failed to clear shifts. Please try again.');
      setIsClearDialogOpen(false);
    } finally {
      setIsClearing(false);
    }
  };

  const handleClearDialogCancel = () => {
    if (!isClearing) {
      setIsClearDialogOpen(false);
    }
  };

  // Handle marking day as closed
  const handleMarkDayClosed = async () => {
    setIsMarkingClosed(true);
    try {
      await addClosedDate(currentWorkingDate, closedReason || undefined);
      toast.success('Day marked as closed', {
        description: `${format(currentWorkingDate, 'EEEE, MMMM d')} is now marked as closed`
      });
      setIsClosedDialogOpen(false);
      setClosedReason('');
      onClosedDatesChange?.();
    } catch (error) {
      toast.error('Failed to mark day as closed', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsMarkingClosed(false);
    }
  };

  // Handle reopening a closed day
  const handleReopenDay = async () => {
    if (!currentDateClosedInfo) return;

    setIsMarkingClosed(true);
    try {
      await removeClosedDate(currentDateClosedInfo.id);
      toast.success('Day reopened', {
        description: `${format(currentWorkingDate, 'EEEE, MMMM d')} is no longer marked as closed`
      });
      onClosedDatesChange?.();
    } catch (error) {
      toast.error('Failed to reopen day', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsMarkingClosed(false);
    }
  };

  // Render day actions section content based on state
  const renderDayActionsContent = () => {
    if (!isEditingMode) {
      return (
        <div className="text-center py-4">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Day Actions Unavailable
          </div>
          <div className="text-xs text-gray-500">
            Enable editing mode to copy or clear day shifts
          </div>
        </div>
      );
    }

    if (!selectedEditingDate) {
      return (
        <div className="text-center py-4">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            No Day Selected
          </div>
          <div className="text-xs text-gray-500 mb-3">
            Select a day in the calendar to start copying or managing shifts
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDaySelect && onDaySelect(new Date())}
            className="text-xs h-7"
          >
            <Calendar className="h-3 w-3 mr-1" />
            Select Today
          </Button>
        </div>
      );
    }

    // Full day actions interface when editing is enabled and day is selected
    return (
      <ScrollArea className="h-full overflow-y-auto overflow-x-hidden no-min-width" scrollHideDelay={0}>
        {/* Working Day Display */}
        <div className="mb-3">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Working Day:</div>
          <div
            className="font-medium text-sm cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
            onClick={() => onDaySelect && onDaySelect(currentWorkingDate)}
            title="Click a day label in the calendar to change"
          >
            {format(currentWorkingDate, 'MMM d, yyyy')}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {shiftsForWorkingDate.length} shift{shiftsForWorkingDate.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Copy Day Section */}
        <div className="space-y-2 mb-3">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Copy Day</div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowCopyDialog(true)}
            disabled={shiftsForWorkingDate.length === 0}
            className="w-full text-xs h-8 px-2"
          >
            <Copy className="h-3 w-3 mr-1 flex-shrink-0" />
            <span className="truncate">
              Copy to... ({shiftsForWorkingDate.length})
            </span>
          </Button>
        </div>

        {/* Save Day Template Section */}
        <div className="space-y-2 mb-3">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Save Template</div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowSaveDayTemplateDialog(true)}
            disabled={shiftsForWorkingDate.length === 0}
            className="w-full text-xs h-8 px-2"
          >
            <Save className="h-3 w-3 mr-1 flex-shrink-0" />
            <span className="truncate">
              Day Template ({shiftsForWorkingDate.length})
            </span>
          </Button>
        </div>

        {/* Clear Day Section */}
        <div className="space-y-2 mb-3">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Clear Day</div>
          <ConfirmationDialog
            trigger={
              <Button
                size="sm"
                variant="destructive"
                disabled={!isEditingMode || shiftsForWorkingDate.length === 0}
                className="w-full text-xs h-8 px-2"
                onClick={() => setIsClearDialogOpen(true)}
              >
                <Trash2 className="h-3 w-3 mr-1 flex-shrink-0" />
                <span className="truncate">
                  Clear ({shiftsForWorkingDate.length})
                </span>
              </Button>
            }
            title="Confirm Day Clearing"
            description={`Are you sure you want to clear all ${shiftsForWorkingDate.length} shift(s) from ${format(currentWorkingDate, 'MMM d, yyyy')}? This action cannot be undone.`}
            confirmText="Clear Shifts"
            cancelText="Cancel"
            onConfirm={handleClearDay}
            onCancel={handleClearDialogCancel}
            variant="destructive"
            loading={isClearing}
            disabled={!isEditingMode || shiftsForWorkingDate.length === 0}
            open={isClearDialogOpen}
            onOpenChange={setIsClearDialogOpen}
          />
        </div>

        {/* Mark Closed Section */}
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Day Status</div>
          {currentDateClosedInfo ? (
            // Day is already marked as closed (custom closure)
            <div className="space-y-2">
              <div className="p-2 rounded bg-destructive/10 border border-destructive/20">
                <div className="flex items-center gap-1.5 text-xs text-destructive font-medium">
                  <CalendarOff className="h-3 w-3" />
                  Closed
                </div>
                {currentDateClosedInfo.reason && (
                  <p className="text-xs text-muted-foreground mt-1 truncate" title={currentDateClosedInfo.reason}>
                    {currentDateClosedInfo.reason}
                  </p>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleReopenDay}
                disabled={isMarkingClosed}
                className="w-full text-xs h-8 px-2"
              >
                <CalendarCheck className="h-3 w-3 mr-1 flex-shrink-0" />
                <span className="truncate">Reopen Day</span>
              </Button>
            </div>
          ) : currentDateClosureStatus.isClosed && currentDateClosureStatus.closureType === 'weekly_schedule' ? (
            // Day is closed via weekly schedule
            <div className="p-2 rounded bg-muted border border-border">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                <CalendarOff className="h-3 w-3" />
                Weekly Closed
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Per schedule
              </p>
            </div>
          ) : (
            // Day is open - show mark closed button
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsClosedDialogOpen(true)}
              disabled={isMarkingClosed}
              className="w-full text-xs h-8 px-2"
            >
              <CalendarOff className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="truncate">Mark Closed</span>
            </Button>
          )}
        </div>
      </ScrollArea>
    );
  };

  return (
    <div className="h-full flex flex-col max-w-[9rem] w-full p-2">
      {/* Top section - Day Actions (40% height) */}
      <div className="min-h-[40%] flex-1 border-b border-border/40 pb-1 mb-1">
        <Card className="border-0 h-full">
          <CardContent className="p-0 h-full overflow-hidden">
            {renderDayActionsContent()}
          </CardContent>
        </Card>
      </div>

      {/* Bottom section - Day Templates (60% height) */}
      <div className="min-h-[60%] flex-1">
        <div className="text-xs font-medium text-muted-foreground mb-1 px-1 truncate">
          Day Templates
        </div>
        <div className="relative mb-1">
          <Input
            value={templateSearchTerm}
            onChange={(e) => setTemplateSearchTerm(e.target.value)}
            className="h-5 pr-6 text-xs !bg-background"
            placeholder="Search..."
            aria-label="Search day templates"
          />
          <Search className="h-3 w-3 absolute right-1.5 top-1 text-muted-foreground" />
        </div>
        <ScrollArea className="h-[calc(100%-40px)] overflow-y-auto overflow-x-hidden" scrollHideDelay={0}>
          {loadingDayTemplates ? (
            <div className="flex items-center justify-center py-2">
              <div className="text-xs text-gray-500">Loading...</div>
            </div>
          ) : filteredDayTemplates.length === 0 ? (
            <div className="text-xs text-gray-500 py-2 text-center">
              {dayTemplates.length === 0 ? 'No templates' : 'No matches'}
            </div>
          ) : (
            <div className="space-y-1">
              {filteredDayTemplates.map(template => (
                <div key={template.id} className="max-w-full overflow-hidden">
                  <DayTemplateItem
                    template={template}
                    compact={true}
                    onEdit={onEditDayTemplate}
                    onDelete={onDeleteDayTemplate}
                  />
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Day Template Dialog */}
      {onSaveDayTemplate && (
        <SaveDayTemplateDialog
          isOpen={showSaveDayTemplateDialog}
          onClose={() => setShowSaveDayTemplateDialog(false)}
          onSave={async (name: string, description: string) => {
            await onSaveDayTemplate(name, description);
            setShowSaveDayTemplateDialog(false);
          }}
          dayShifts={shiftsForWorkingDate}
          selectedDate={currentWorkingDate}
        />
      )}

      {/* Advanced Copy Dialog */}
      <AdvancedShiftCopyDialog
        open={showCopyDialog}
        onOpenChange={setShowCopyDialog}
        dayShifts={shiftsForWorkingDate}
        selectedDate={currentWorkingDate}
        businessId={businessId}
        onShiftCreated={onShiftCreated || (() => {})}
      />

      {/* Mark Day Closed Dialog */}
      <Dialog open={isClosedDialogOpen} onOpenChange={setIsClosedDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarOff className="h-5 w-5" />
              Mark Day as Closed
            </DialogTitle>
            <DialogDescription>
              Mark {format(currentWorkingDate, 'EEEE, MMMM d, yyyy')} as closed
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {shiftsForWorkingDate.length > 0 && (
              <Alert variant="default" className="border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800">
                <AlertDescription className="text-amber-800 dark:text-amber-200 text-sm">
                  There {shiftsForWorkingDate.length === 1 ? 'is' : 'are'}{' '}
                  <strong>{shiftsForWorkingDate.length} shift{shiftsForWorkingDate.length !== 1 ? 's' : ''}</strong>{' '}
                  scheduled for this day. They will not be automatically removed.
                </AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="closed-reason">Reason (optional)</Label>
              <Input
                id="closed-reason"
                placeholder="e.g., Holiday, Renovation, Training..."
                value={closedReason}
                onChange={(e) => setClosedReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsClosedDialogOpen(false)} disabled={isMarkingClosed}>
              Cancel
            </Button>
            <Button onClick={handleMarkDayClosed} disabled={isMarkingClosed}>
              {isMarkingClosed ? 'Marking...' : 'Mark as Closed'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DayGroupActions;